import { all, call, takeLatest, put, select } from 'redux-saga/effects';
import { formControlActions } from 'store/formControlSlice/formControlSlice';
import { transactionActions } from 'store/transactionsSlice/transactionsSlice';
import { selectFormById } from '../formControlSelectors';
import { tableControlActions } from 'store/tableControlSlice/tableControlSlice';
import { notificationHandler as notificationsHandler, readFromObj, writeInObj } from '../../../utils/utils';
import { autoPopulateTransactionLookupValue } from 'store/formControlSlice/formControlSagas/formControlSagaHelper';
import { EXTERNAL_ANNOUNCEMENT_SOURCE } from 'utils/constants';
import { preserveRetiredLookupValues } from 'utils/lookupHelper';
import { axiosAiq } from 'config';

const DEFAULT_FORM_FIELDS = Object.freeze({
    GATE: {
        '@type': 'Field',
        caption: 'Gate',
        dataField: 'gate',
        dataType: 'string',
        editable: true,
        header: 'General',
        automationField: false,
    },
    STARTED_TIME: {
        '@type': 'Field',
        caption: 'Started (UTC)',
        dataField: 'startedUtcDateTime',
        dataType: 'datetime',
        editable: true,
        format: 'dd.MM.yyyy. HH:mm:ss',
        header: 'General',
        automationField: false,
    },
    STARTED_LOCAL_TIME: {
        '@type': 'Field',
        caption: 'Started',
        dataField: 'startedDateTime',
        dataType: 'datetime',
        editable: false,
        format: 'dd.MM.yyyy. HH:mm:ss',
        header: 'General',
        automationField: false,
    },
    TRANSACTION_ID: {
        '@type': 'Field',
        caption: 'Transaction Id',
        dataField: 'id',
        dataType: 'string',
        editable: false,
        header: 'General',
        automationField: false,
    },
    TRANSACTION_SOURCE: {
        '@type': 'Field',
        caption: 'Source',
        dataField: 'transactionSource',
        dataType: 'string',
        editable: false,
        header: 'General',
        automationField: false,
    },
    STATUS: {
        '@type': 'Field',
        caption: 'Status',
        dataField: 'status',
        dataType: 'string',
        editable: false,
        header: 'General',
        automationField: false,
    },
    DESK: {
        '@type': 'Field',
        caption: 'Desk',
        dataField: 'desk.name',
        dataType: 'string',
        editable: false,
        header: 'General',
        automationField: false,
    },
    TECHNICAL_OPERATOR: {
        '@type': 'Field',
        caption: 'Technical Operator',
        dataField: 'account.username',
        dataType: 'string',
        editable: false,
        header: 'General',
        automationField: false,
    },
    LOCATION: {
        '@type': 'Field',
        caption: 'Location',
        dataField: 'gate.location.fullName',
        dataType: 'string',
        editable: false,
        header: 'General',
        automationField: false,
    },
    FINISHED_TIME: {
        '@type': 'Field',
        caption: 'Finished',
        dataField: 'finishedDateTime',
        dataType: 'datetime',
        editable: false,
        format: 'dd.MM.yyyy. HH:mm:ss',
        header: 'General',
        automationField: false,
    },
    FINISHED_UTC_TIME: {
        '@type': 'Field',
        caption: 'Finished (UTC)',
        dataField: 'finishedUtcDateTime',
        dataType: 'datetime',
        editable: false,
        format: 'dd.MM.yyyy. HH:mm:ss',
        header: 'General',
        automationField: false,
    },
    TIME_ZONE: {
        '@type': 'Field',
        caption: 'Time zone',
        dataField: 'gate.location.timeZone',
        dataType: 'string',
        editable: false,
        header: 'General',
        automationField: false,
    },
});

export function prepareTransactionForSubmit(form) {
    const {
        form: { values },
        transactionData,
        transactionIdAnnouncementUsed,
        internalIdAnnouncementUsed,
        externalAnnouncements,
    } = form;

    const transactionToSubmit = structuredClone(transactionData);
    for (let fieldId of Object.keys(values)) {
        const formFieldValue = values[fieldId];
        if (formFieldValue === '' || formFieldValue === null) {
            const receivedFieldValue = readFromObj(transactionToSubmit, fieldId);
            if (!receivedFieldValue) continue;
        }

        writeInObj(transactionToSubmit, fieldId, values[fieldId]);
    }
    transactionIdAnnouncementUsed && (transactionToSubmit.announcement.transactionIdUsed = transactionIdAnnouncementUsed);
    internalIdAnnouncementUsed && (transactionToSubmit.internalIdAnnouncementUsed = internalIdAnnouncementUsed);
    const bisonAnnouncements = externalAnnouncements?.[EXTERNAL_ANNOUNCEMENT_SOURCE.BISON];
    if (bisonAnnouncements?.length) transactionToSubmit.externalAnnouncements = bisonAnnouncements;
    return transactionToSubmit;
}

export function* initializeEditTransactionForm({ payload: transactionRecord }) {
    try {
        const transactionId = transactionRecord.id;
        yield put(formControlActions.initializeNewTransactionFormStart());
        const response = yield axiosAiq.get(`/transactions/trucks/${transactionId}`);
        const transactionData = response.data;
        transactionData.gate.displayName = `${transactionData?.gate?.name} (${transactionData?.gate?.direction})`;
        transactionData.startedUtcDateTime = transactionRecord.startedUtcDateTime;
        transactionData.finishedUtcDateTime = transactionRecord.finishedUtcDateTime;
        yield put(
            formControlActions.initializeNewTransactionFormSuccess({
                transactionData,
                transactionId,
            })
        );
        yield all([call(fetchTransactionFormConfig, { transactionData, transactionRecord })]);
    } catch (err) {
        yield call(notificationsHandler, {
            err,
            title: 'Error initializing form',
        });
        yield put(formControlActions.initializeNewTransactionFormFailed(err.message));
    }
}

export function* fetchTransactionFormConfig({ transactionData }) {
    try {
        const locationId = transactionData?.gate?.location?.id;
        const gateId = transactionData?.gate?.id;
        const companyId = transactionData?.gate?.location?.company?.id;
        const transactionId = transactionData?.id;

        yield put(formControlActions.fetchTransactionFormConfigStart());

        const gatesResponse = yield axiosAiq.get(`/locations/${locationId}/gates`);
        const gates = gatesResponse.data;

        const [formConfigResponse] = yield all([
            axiosAiq(`/transactions/trucks/form/fields`, {
                params: { gateId, locationId, companyId },
            }),
        ]);

        const { data: transactionFormConfig } = formConfigResponse;

        // A value the location has since deactivated is absent from the lookup the backend sends, so
        // re-add it here or the field renders empty on an older transaction.
        const fieldsWithRetainedValues = preserveRetiredLookupValues(transactionFormConfig.fields, transactionData);

        const newFormFields = fieldsWithRetainedValues.reduce((newFormFields, field) => {
            if (field.header !== 'General') {
                field.validationField = null;
                newFormFields.push(field);
            }
            return newFormFields;
        }, []);

        const gateFieldLookup = {
            dataSource: gates.map((el) => ({ ...el, displayName: `${el.name} (${el.direction})` })),
            displayExpr: 'displayName',
        };
        newFormFields.unshift(DEFAULT_FORM_FIELDS.FINISHED_TIME);
        newFormFields.unshift(DEFAULT_FORM_FIELDS.STARTED_LOCAL_TIME);
        newFormFields.unshift(DEFAULT_FORM_FIELDS.TIME_ZONE);
        newFormFields.unshift(DEFAULT_FORM_FIELDS.FINISHED_UTC_TIME);

        newFormFields.unshift(DEFAULT_FORM_FIELDS.STARTED_TIME);
        newFormFields.unshift({ ...DEFAULT_FORM_FIELDS.GATE, lookup: gateFieldLookup });
        newFormFields.unshift(DEFAULT_FORM_FIELDS.LOCATION);
        newFormFields.unshift(DEFAULT_FORM_FIELDS.TECHNICAL_OPERATOR);
        newFormFields.unshift(DEFAULT_FORM_FIELDS.DESK);
        newFormFields.unshift(DEFAULT_FORM_FIELDS.STATUS);
        newFormFields.unshift(DEFAULT_FORM_FIELDS.TRANSACTION_SOURCE);
        newFormFields.unshift(DEFAULT_FORM_FIELDS.TRANSACTION_ID);

        transactionFormConfig.fields = newFormFields;

        yield put(
            formControlActions.fetchTransactionFormConfigSuccess({
                transactionId,
                transactionFormConfig,
                isSplitViewMode: false,
            })
        );
    } catch (err) {
        yield call(notificationsHandler, {
            err,
            action: formControlActions.fetchTransactionFormConfigFailed,
            title: 'Error fetching form config file',
        });
    }
}

export function* removeTransaction({ payload }) {
    const { transactionId } = payload;
    try {
        const response = yield axiosAiq.delete(`/transactions/trucks/delete/${transactionId}`);

        if (response.status === 200) {
            yield put(transactionActions.onEditTransactionModalDelete(false));
            yield put(transactionActions.onEditTransactionModal(false));
            yield put(
                tableControlActions.fetchTableDataStart({
                    urlPath: `/v2/transactions/trucks/recent?page=0&size=750`,
                    tableId: 'all_transactions',
                    fetchColumnsConfig: false,
                })
            );
            yield call(notificationsHandler, {
                title: `Record #${transactionId} is removed from Birdseye Lasta, but it is not removed in the Sheets.`,
                variant: 'success',
            });
        }
    } catch (err) {
        yield call(notificationsHandler, {
            err,
            title: 'Error deleting transaction',
        });
    }
}

export function* editFormStart({ payload }) {
    const { transactionId } = payload;
    let form = yield select(selectFormById(transactionId));
    try {
        yield put(formControlActions.setSubmitFormInProgress({ formId: transactionId }));
        const errors = form?.form?.errors;

        let firstInvalidField;
        for (let fieldName of Object.keys(errors)) {
            if (errors[fieldName]) {
                yield put(
                    formControlActions.setFieldTouched({
                        formId: transactionId,
                        fieldName,
                    })
                );

                if (firstInvalidField) continue;
                firstInvalidField = fieldName;
            }
        }

        if (firstInvalidField) {
            yield put(formControlActions.setSubmitFormInProgress({ formId: transactionId, inProgress: false }));
            yield call(notificationsHandler, {
                title: 'Fill all required fields!',
                variant: 'warning',
            });
            return;
        }

        let filledTransactionToSubmit;

        const transactionToSubmit = yield call(prepareTransactionForSubmit, form);
        transactionToSubmit.started = transactionToSubmit.startedUtcDateTime + 'Z';
        transactionToSubmit.finished = transactionToSubmit.finishedUtcDateTime + 'Z';
        transactionToSubmit.gateDirection = form?.form?.values?.gate?.direction;
        filledTransactionToSubmit = yield call(autoPopulateTransactionLookupValue, form, transactionToSubmit);

        const config = {
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json, application/javascript, text/javascript, text/json',
            },
        };

        const response = yield axiosAiq.put('/transactions/trucks', filledTransactionToSubmit, config);

        if (response.status === 200) {
            yield put(formControlActions.setSubmitFormInProgress({ formId: transactionId, inProgress: false }));

            yield put(
                tableControlActions.fetchTableDataStart({
                    urlPath: `/v2/transactions/trucks/recent?page=0&size=750`,
                    tableId: 'all_transactions',
                    fetchColumnsConfig: false,
                })
            );
            yield put(transactionActions.onEditTransactionModal(false));
            yield put(formControlActions.removeForm(transactionId));
            yield call(notificationsHandler, {
                title: `Record #${transactionId} is updated in Birdseye Lasta, it is not updated in the Sheets.`,
                variant: 'success',
            });
        }
    } catch (err) {
        yield put(formControlActions.setSubmitFormInProgress({ formId: transactionId, inProgress: false }));
        yield call(notificationsHandler, {
            err,
            title: `Error updating #${transactionId} in Birdseye Lasta.`,
            variant: 'warning',
        });
    }
}

export function* onInitializeEditTransactionForm() {
    yield takeLatest(formControlActions.initializeEditTransactionForm.type, initializeEditTransactionForm);
}

export function* onEditFormStart() {
    yield takeLatest(formControlActions.editFormStart.type, editFormStart);
}

export function* onRemoveTransaction() {
    yield takeLatest(formControlActions.removeTransaction.type, removeTransaction);
}
